import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import Person2Icon from "@mui/icons-material/Person2";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import copy from "copy-text-to-clipboard";
import { IFInvoiceWithDetailsObj } from "@/types/interfaces/crm";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { formatInTorontoTime } from "../../utility/utils";

const highlightMatch = (text?: string | null, query?: string | null) => {
  if (!text) return "";
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={index}>{part}</mark> : <span key={index}>{part}</span>,
  );
};

interface Props {
  invoice: IFInvoiceWithDetailsObj;
  selectedInvoice: string;
  searchText: string;
  setSelectedInvoiceIndex: (index: number) => void;
  index: number;
  selectedInvoiceIndex: number;
}

export default function InvoiceCard({
  invoice,
  selectedInvoice,
  searchText,
  setSelectedInvoiceIndex,
  index,
  selectedInvoiceIndex,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const createdTime = useMemo(
    () => (invoice?.createdTime ? new Date(invoice?.createdTime) : null),
    [invoice?.createdTime],
  );

  return (
    <Stack
      id={`invoice-card-${invoice.invoiceId}`}
      role="button"
      ref={(el) => {
        if (selectedInvoice === invoice.invoiceId?.toString() && el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }}
      tabIndex={0}
      className="container"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setSearchParams({ invoiceId: invoice.invoiceId?.toString() });
          setSelectedInvoiceIndex(index);
        }
      }}
      onClick={() => (setSearchParams({ invoiceId: invoice.invoiceId?.toString() }), setSelectedInvoiceIndex(index))}
      sx={{
        cursor: "pointer",
        borderLeft: selectedInvoiceIndex === index ? "4px solid #3C82F6" : "4px solid #fff",
        bgcolor: selectedInvoice === invoice.invoiceId?.toString() ? "#e7f2ff" : "white",
        boxShadow:
          selectedInvoice === invoice.invoiceId?.toString() ? "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" : "none",
        padding: "1rem",
        width: "100%",
        height: "auto",
        borderRadius: "6px",
      }}
    >
      <Stack
        direction={"row"}
        sx={{
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography textColor={"#334155"} fontFamily={"Inter"} fontWeight={700} fontSize={"16px"}>
            #{invoice?.record}{" "}
            <span style={{ fontWeight: 400 }}>| {highlightMatch(invoice?.invoiceNumber, searchText)}</span>
          </Typography>
          {invoice?.invoiceNumber && (
            <Tooltip variant="outlined" title="Copy invoice number" arrow>
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (copy(invoice.invoiceNumber!)) {
                    toast.success("Invoice number copied to your clipboard!");
                  }
                }}
              >
                <ContentCopyOutlinedIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Typography textColor="#64748B" fontFamily={"Inter"} fontWeight={400} lineHeight={"24px"} fontSize={"12px"}>
          {createdTime && formatDistanceToNow(createdTime)}
        </Typography>
      </Stack>
      <Typography
        startDecorator={<Person2Icon sx={{ fontSize: "12px", color: "#64748B", height: "14px" }} />}
        fontSize={"0.8rem"}
        fontWeight={400}
        lineHeight={"20px"}
      >
        {highlightMatch(invoice.profileName, searchText)}
      </Typography>
      <Typography
        startDecorator={<CalendarMonthIcon sx={{ fontSize: "12px", color: "#64748B", height: "14px" }} />}
        textColor="#64748B"
        fontFamily={"Inter"}
        fontWeight={400}
        lineHeight={"24px"}
        fontSize={"12px"}
      >
        {createdTime && formatInTorontoTime(createdTime, "dd MMM yyyy, h:mm a zzz")}
      </Typography>
    </Stack>
  );
}
